import { useState, useRef, useEffect, useLayoutEffect } from 'react'
import type { Devanning } from '../types'
import { fmtDate } from '../types'
import { companyAddressLines, companyRegistrationLine, type CompanySettings } from '../../../lib/companySettings'
import { AddressField, blankAddress, type PickedAddress } from './AddressPicker'
import { fmtIssuedAt } from './DeliveryCollectionNotePDF'
import { suggestFulfilmentForNote, noteKindForMethodCode, commonMethodId, type FulfilmentType, type DeliveryMethodRow } from '../../../lib/fulfilmentType'

// Z50.X (5th pass): WYSIWYG note editor -- the on-screen layout below is a plain HTML/CSS
// twin of DeliveryCollectionNotePDF.tsx's react-pdf layout (same section order, same
// borders/proportions), NOT the same component -- react-pdf's <Document>/<Page>/<View>/<Text>
// render only to a PDF stream, there is no way to make them an interactive, contentEditable
// browser DOM. The actual PDF is still generated from this same draft data via that
// react-pdf component; this view exists only so staff can edit in place while seeing
// (approximately) what the printed note will look like, at A4 proportions.
//
// Click-to-edit pattern (InlineText/InlineDate/etc.) matches Schedule.tsx's established
// convention exactly (2026-06-23 Warehouse WYSIWYG grid) rather than contentEditable or a
// rich-text library: a field shows its value as plain text, click swaps in a real input,
// blur/Enter commits, Escape cancels.

const AUTO_FIELD_KEYS: AutoFieldKey[] = ['nop', 'weightKg', 'cbm', 'description', 'summary', 'remarks', 'authoriseTo']
const AUTO_FIELD_LABELS: Record<AutoFieldKey, string> = {
  nop: 'NOP', weightKg: 'Weight', cbm: 'Cube', remarks: 'Remarks', authoriseTo: 'Authorise To',
  description: 'Description', summary: 'Summary (pallets)',
}


export type { ItemGroupLite, AutoFieldKey, NoteDraft } from '../../../lib/deliveryNoteDraft'
import type { ItemGroupLite, AutoFieldKey, NoteDraft } from '../../../lib/deliveryNoteDraft'

const cellCls = 'border border-blue-400 rounded px-1 py-0.5 text-[10px] focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white dark:bg-gray-700 dark:text-white'

function InlineText({ value, onCommit, onCancel, placeholder, listId }: {
  value: string; onCommit: (v: string) => void; onCancel: () => void; placeholder?: string; listId?: string
}) {
  const [v, setV] = useState(value)
  const ref = useRef<HTMLInputElement>(null)
  useEffect(() => { ref.current?.focus(); ref.current?.select() }, [])
  return <input ref={ref} className={`${cellCls} w-full`} value={v} placeholder={placeholder} list={listId}
    onChange={e => setV(e.target.value)}
    onBlur={() => onCommit(v)}
    onKeyDown={e => { if (e.key === 'Enter') onCommit(v); if (e.key === 'Escape') onCancel() }} />
}

function InlineNumber({ value, onCommit, onCancel }: { value: string; onCommit: (v: string) => void; onCancel: () => void }) {
  const [v, setV] = useState(value)
  const ref = useRef<HTMLInputElement>(null)
  useEffect(() => { ref.current?.focus(); ref.current?.select() }, [])
  return <input ref={ref} type="number" className={`${cellCls} w-20 text-right`} value={v}
    onChange={e => setV(e.target.value)}
    onBlur={() => onCommit(v)}
    onKeyDown={e => { if (e.key === 'Enter') onCommit(v); if (e.key === 'Escape') onCancel() }} />
}

function InlineDate({ value, onCommit, onCancel }: { value: string; onCommit: (v: string) => void; onCancel: () => void }) {
  const [v, setV] = useState(value)
  const ref = useRef<HTMLInputElement>(null)
  useEffect(() => { ref.current?.focus() }, [])
  return <input ref={ref} type="date" className={cellCls} value={v}
    onChange={e => setV(e.target.value)}
    onBlur={() => onCommit(v)}
    onKeyDown={e => { if (e.key === 'Enter') onCommit(v); if (e.key === 'Escape') onCancel() }} />
}

// Wrapping + auto-height: grows with its content (no fixed row count, no inner scrollbar), and an
// unbroken reference number still breaks instead of running past the edge.
const WRAP_CLS = 'whitespace-pre-wrap [overflow-wrap:anywhere]'

function AutoGrowTextArea({ value, className, focusOnMount = false, ...rest }:
  React.TextareaHTMLAttributes<HTMLTextAreaElement> & { value: string; focusOnMount?: boolean }) {
  const ref = useRef<HTMLTextAreaElement>(null)
  useEffect(() => { if (focusOnMount) { ref.current?.focus(); ref.current?.select() } }, [focusOnMount])
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight}px`
  }, [value])
  return <textarea ref={ref} rows={1} value={value} className={`${className ?? ''} ${WRAP_CLS} resize-none overflow-hidden`} {...rest} />
}

function InlineTextArea({ value, onCommit, onCancel }: { value: string; onCommit: (v: string) => void; onCancel: () => void; rows?: number }) {
  const [v, setV] = useState(value)
  return <AutoGrowTextArea focusOnMount className={`${cellCls} w-full`} value={v}
    onChange={e => setV(e.target.value)}
    onBlur={() => onCommit(v)}
    onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); onCommit(v) } if (e.key === 'Escape') onCancel() }} />
}

// Click-to-edit display wrapper -- shows plain text (or a placeholder dash) until clicked,
// swaps to the given inline editor, commits on blur/Enter. Matches Schedule.tsx's pattern.
function EditableField({ value, display, onCommit, editor, className }: {
  value: string
  display?: string
  onCommit: (v: string) => void
  editor: (props: { value: string; onCommit: (v: string) => void; onCancel: () => void }) => React.ReactElement
  className?: string
}) {
  const [editing, setEditing] = useState(false)
  if (editing) {
    return editor({ value, onCommit: v => { onCommit(v); setEditing(false) }, onCancel: () => setEditing(false) })
  }
  return (
    <span onClick={() => setEditing(true)}
      className={`group cursor-text hover:bg-yellow-50 dark:hover:bg-yellow-900/20 rounded px-0.5 -mx-0.5 inline-block max-w-full align-top min-w-[1em] min-h-[1em] ${WRAP_CLS} ${className || ''}`}>
      {(display || value) || <span className="text-gray-300 italic">—</span>}
      <span className="ml-0.5 opacity-0 group-hover:opacity-100 text-[9px] text-gray-400">✎</span>
    </span>
  )
}

// Auto-field: same click-to-edit affordance, plus a sync/override icon and reset-to-auto.
function AutoField({ auto, override, display, onOverride, onReset, editor, plain = false }: {
  plain?: boolean
  auto: string
  override: string | null
  display?: string
  onOverride: (v: string) => void
  onReset: () => void
  editor: (props: { value: string; onCommit: (v: string) => void; onCancel: () => void }) => React.ReactElement
}) {
  const isOverridden = override !== null
  const current = isOverridden ? override : auto
  const [editing, setEditing] = useState(false)
  return (
    <span className="inline-flex items-start gap-1 max-w-full">
      {editing ? editor({ value: current, onCommit: v => { onOverride(v); setEditing(false) }, onCancel: () => setEditing(false) }) : (
        <span onClick={() => setEditing(true)}
          className={`group cursor-text hover:bg-yellow-50 dark:hover:bg-yellow-900/20 rounded px-0.5 -mx-0.5 inline-block min-w-0 max-w-full min-h-[1em] ${WRAP_CLS}`}>
          {(display || current) || <span className="text-gray-300 italic">—</span>}
        </span>
      )}
      {!plain && <span title={isOverridden ? 'Manually overridden -- click to reset to auto-computed value' : 'Auto-computed from devanning data'}
        onClick={isOverridden ? onReset : undefined}
        className={`shrink-0 text-[10px] leading-none ${isOverridden ? 'text-amber-500 cursor-pointer hover:text-amber-600' : 'text-gray-300'}`}>
        {isOverridden ? '✏️' : '🔄'}
      </span>}
    </span>
  )
}

interface NoteWysiwygProps {
  draft: NoteDraft
  onChange: (patch: Partial<NoteDraft>) => void
  groups?: ItemGroupLite[]
  dispatchedElsewhere?: number
  autoNop?: number
  autoWeightKg?: number | null
  autoCbm?: number | null
  autoRemarks?: string
  autoAuthoriseTo?: string
  autoDescription?: string
  autoSummary?: string
  devanning?: Devanning | null
  company: CompanySettings
  jobRef: string
  serialNo: number | null
  // The staff member who'll be stamped as issuer if this note is generated right now --
  // resolveBoxtopEmployeeName(userEmail), same lookup as the notes list's "Issued by" line.
  // Not editable here: issued_by_name/issued_at are audit fields, frozen at actual generate
  // time in Notes.tsx's generatePdfFor, not something a draft edit should be able to alter.
  issuerName: string
  // The devanning's own warehouse, as a ready-to-use address -- the "smart default" for
  // Delivery's Release To / Collection's Collect From. Overridable, resettable via 🔄.
  autoWarehouseAddress?: PickedAddress | null
  // pricing_delivery_methods.id -> default_fulfilment_type -- the clean path
  // suggestFulfilmentType prefers over the free-text keyword heuristic.
  methods: DeliveryMethodRow[]
  // Z44: the devanning-related props above are optional. A note raised on the Job -> Transport tab has no devanning: it passes none of
  // them and sets plain, so every quantity is typed in (no Auto/Overridden badges, no "Data Sources" panel, no source-row banner).
  plain?: boolean
  // Shown instead of the source-row banner when plain (e.g. the container picker) and in place of devanning.container_no at the bottom.
  headerExtra?: React.ReactNode
  containerNo?: string | null
}

export function NoteWysiwygEditor(props: NoteWysiwygProps) {
  const { draft, onChange, company, jobRef, serialNo, issuerName, methods, headerExtra } = props
  const plain = props.plain ?? false
  const groups = props.groups ?? []
  const dispatchedElsewhere = props.dispatchedElsewhere ?? 0
  const autoNop = props.autoNop ?? 0
  const autoWeightKg = props.autoWeightKg ?? null
  const autoCbm = props.autoCbm ?? null
  const autoRemarks = props.autoRemarks ?? ''
  const autoAuthoriseTo = props.autoAuthoriseTo ?? ''
  const autoDescription = props.autoDescription ?? ''
  const autoSummary = props.autoSummary ?? ''
  const devanning = props.devanning ?? null
  const autoWarehouseAddress = props.autoWarehouseAddress ?? null
  const containerNo = props.containerNo ?? devanning?.container_no ?? null
  const selectedGroups = groups.filter(g => g.key === draft.sourceKey)
  const sourceGroup = selectedGroups[0]
  const autoMethodId = commonMethodId(selectedGroups.flatMap(g => g.deliveryMethodIds))
  const methodId = draft.deliveryMethodId ?? autoMethodId
  const method = methods.find(m => m.id === methodId)
  // Note kind follows the chosen method; with no method at all, whatever the draft carries.
  const noteType = method ? noteKindForMethodCode(method.code) : draft.noteType
  const isSelfOrSupplier = noteType === 'COLLECTION_SELF' || noteType === 'COLLECTION_SUPPLIER'
  const releaseToLabel = noteType === 'COLLECTION_SELF' ? 'Customer Name'
    : noteType === 'COLLECTION_SUPPLIER' ? 'Supplier Name' : 'Release To (courier)'
  const title = noteType === 'COLLECTION_SELF' ? 'Release/Delivery Note (Self-Pickup)'
    : noteType === 'COLLECTION_SUPPLIER' ? 'Release/Delivery Note (Supplier Pickup)'
    : 'Release/Delivery Note'
  const methodIsOverridden = draft.deliveryMethodId !== null && autoMethodId !== null && draft.deliveryMethodId !== autoMethodId
  const methodSelect = (cls: string) => (
    <>
      <select className={cls} value={methodId || ''}
        onChange={e => onChange({ deliveryMethodId: e.target.value || null })}>
        <option value="" disabled>Choose delivery method…</option>
        {methods.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
      </select>
      {methodIsOverridden && (
        <button onClick={() => onChange({ deliveryMethodId: null })} title="Reset to the items' delivery method"
          className="text-amber-500 hover:text-amber-600 text-[9px]">✏️</button>
      )}
    </>
  )

  const nop = draft.nopOverride ?? autoNop
  const weightKg = draft.weightKgOverride ?? autoWeightKg
  const cbm = draft.cbmOverride ?? autoCbm
  const remarks = draft.remarksOverride ?? autoRemarks
  const authoriseTo = draft.authoriseToOverride ?? autoAuthoriseTo
  const description = draft.descriptionOverride ?? autoDescription
  const summary = draft.summaryOverride ?? autoSummary
  // A fresh suggestion from whichever groups are currently selected (recomputed live as staff
  // picking the source row), never auto-applied.
  const autoFulfilment = suggestFulfilmentForNote(
    methodId, methods,
    selectedGroups.flatMap(g => g.deliveryMethods.map((text, i) => ({ deliveryMethodId: g.deliveryMethodIds[i], deliveryMethodText: text }))),
  )
  const fulfilmentType = draft.fulfilmentType ?? autoFulfilment.type
  const fulfilmentIsOverridden = draft.fulfilmentType !== null
  const showDeliverTo = fulfilmentType === 'COLLECTION_AND_DELIVERY'

  const goodsLines = draft.marks.split('\n').map(s => s.trim()).filter(Boolean)
  const marksDisplay = goodsLines.length ? goodsLines.join(', ') : ''

  function updateMarksLine(text: string) {
    onChange({ marks: text })
  }

  return (
    <div className="flex flex-col md:flex-row gap-4">
      {/* ── Mobile fallback: plain stacked fields, no A4 canvas -- WYSIWYG doesn't fit
          below md width. Same draft/onChange, just ordinary inputs. ── */}
      <div className="md:hidden space-y-3">
        <div><label className="block text-xs text-gray-500 mb-1">Customer Ref</label>
          <input className="w-full border rounded px-2 py-1.5 text-sm dark:bg-gray-700" value={draft.customerRef} onChange={e => onChange({ customerRef: e.target.value })} /></div>
        <div><label className="block text-xs text-gray-500 mb-1">Delivery Method</label>
          {methodSelect('w-full border rounded px-2 py-1.5 text-sm dark:bg-gray-700')}</div>
        <div><label className="block text-xs text-gray-500 mb-1">
            Fulfilment Type {autoFulfilment.reason && <span className="text-amber-600 dark:text-amber-400 font-normal">({autoFulfilment.reason})</span>}
          </label>
          <select className="w-full border rounded px-2 py-1.5 text-sm dark:bg-gray-700"
            value={fulfilmentType || ''} onChange={e => onChange({ fulfilmentType: (e.target.value || null) as FulfilmentType | null })}>
            <option value="" disabled>Choose…</option>
            <option value="COLLECTION_ONLY">Collection Only</option>
            <option value="COLLECTION_AND_DELIVERY">Collection + Delivery</option>
          </select></div>
        <div><label className="block text-xs text-gray-500 mb-1">{releaseToLabel}</label>
          <input className="w-full border rounded px-2 py-1.5 text-sm dark:bg-gray-700"
            value={draft.releaseTo.name} onChange={e => onChange({ releaseTo: { ...draft.releaseTo, name: e.target.value } })}
            placeholder="Use desktop view for address book / free text" /></div>
        {isSelfOrSupplier && (
          <>
            <div><label className="block text-xs text-gray-500 mb-1">Vehicle Reg</label>
              <input className="w-full border rounded px-2 py-1.5 text-sm dark:bg-gray-700" value={draft.vehicleReg} onChange={e => onChange({ vehicleReg: e.target.value })} /></div>
            <div><label className="block text-xs text-gray-500 mb-1">Driver Name</label>
              <input className="w-full border rounded px-2 py-1.5 text-sm dark:bg-gray-700" value={draft.driverName} onChange={e => onChange({ driverName: e.target.value })} /></div>
          </>
        )}
        {showDeliverTo && (
          <>
            <div><label className="block text-xs text-gray-500 mb-1">Deliver To</label>
              <input className="w-full border rounded px-2 py-1.5 text-sm dark:bg-gray-700" value={draft.deliverTo.name}
                onChange={e => onChange({ deliverTo: { ...draft.deliverTo, name: e.target.value } })}
                placeholder="Use desktop view for address book / free text" /></div>
            <div><label className="block text-xs text-gray-500 mb-1">Deliver-to Phone</label>
              <input className="w-full border rounded px-2 py-1.5 text-sm dark:bg-gray-700" value={draft.deliverTo.phone || ''} onChange={e => onChange({ deliverTo: { ...draft.deliverTo, phone: e.target.value } })} /></div>
          </>
        )}
        <div><label className="block text-xs text-gray-500 mb-1">Date</label>
          <input type="date" className="w-full border rounded px-2 py-1.5 text-sm dark:bg-gray-700" value={draft.scheduledDate} onChange={e => onChange({ scheduledDate: e.target.value })} /></div>
        <div><label className="block text-xs text-gray-500 mb-1">Marks &amp; Numbers</label>
          <AutoGrowTextArea className="w-full border rounded px-2 py-1.5 text-sm dark:bg-gray-700" value={draft.marks} onChange={e => onChange({ marks: e.target.value })} /></div>
        <div className="grid grid-cols-3 gap-2">
          <div><label className="block text-xs text-gray-500 mb-1">NOP</label>
            <input type="number" className="w-full border rounded px-2 py-1.5 text-sm dark:bg-gray-700" value={nop} onChange={e => onChange({ nopOverride: e.target.value === '' ? null : Number(e.target.value) })} /></div>
          <div><label className="block text-xs text-gray-500 mb-1">Weight</label>
            <input type="number" className="w-full border rounded px-2 py-1.5 text-sm dark:bg-gray-700" value={weightKg ?? ''} onChange={e => onChange({ weightKgOverride: e.target.value === '' ? null : Number(e.target.value) })} /></div>
          <div><label className="block text-xs text-gray-500 mb-1">Cube</label>
            <input type="number" className="w-full border rounded px-2 py-1.5 text-sm dark:bg-gray-700" value={cbm ?? ''} onChange={e => onChange({ cbmOverride: e.target.value === '' ? null : Number(e.target.value) })} /></div>
        </div>
        <div><label className="block text-xs text-gray-500 mb-1">Remarks</label>
          <AutoGrowTextArea className="w-full border rounded px-2 py-1.5 text-sm dark:bg-gray-700" value={remarks} onChange={e => onChange({ remarksOverride: e.target.value })} /></div>
        <div><label className="block text-xs text-gray-500 mb-1">Notes</label>
          <AutoGrowTextArea className="w-full border rounded px-2 py-1.5 text-sm dark:bg-gray-700" value={draft.notes} onChange={e => onChange({ notes: e.target.value })} /></div>
      </div>

      {/* ── Desktop WYSIWYG canvas ── */}
      <div className="hidden md:block flex-1 min-w-0">
        {plain ? (headerExtra ?? null) : (
        <div className="mb-2 text-xs text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-1.5">
          <strong>Source row:</strong> {sourceGroup ? `${sourceGroup.key}${sourceGroup.clientRefSuffix ? ` (${sourceGroup.clientRefSuffix})` : ''}` : '—'}
          {sourceGroup && <> · {sourceGroup.cartons} ctns{dispatchedElsewhere > 0 ? ` · ${dispatchedElsewhere} already dispatched on other notes` : ''} · this note: {nop} ctns</>}
        </div>
        )}

        {(
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg rounded-tl-none bg-white p-6 text-black mx-auto"
            style={{ fontFamily: 'Helvetica, Arial, sans-serif', fontSize: 11, aspectRatio: '210 / 297', maxWidth: 700, overflowY: 'auto' }}>

            {/* Letterhead -- read-only, from company_settings */}
            <div className="flex justify-between items-start mb-3">
              <div className="text-lg font-bold">{company.logo_url ? <img src={company.logo_url} alt="" className="h-8" /> : 'CY UK'}</div>
              <div className="text-right text-[9px] leading-tight">
                <div className="font-bold text-[10px]">{company.legal_name}</div>
                {companyAddressLines(company).map((l, i) => <div key={i}>{l}</div>)}
                {company.phone && <div>T: {company.phone}</div>}
              </div>
            </div>

            <div className="flex justify-between items-end mb-2">
              <div>
                <div className="text-xl font-bold">{title}</div>
                <div className="mt-1 flex items-center gap-1 text-[9px]">
                  <span className="text-gray-500">Delivery Method:</span>
                  {methodSelect('border border-gray-300 rounded px-1 py-0.5 dark:bg-gray-700')}
                </div>
                <div className="mt-1 flex items-center gap-1 text-[9px]">
                  <span className="text-gray-500">Fulfilment:</span>
                  <select className="border border-gray-300 rounded px-1 py-0.5 dark:bg-gray-700"
                    value={fulfilmentType || ''}
                    onChange={e => onChange({ fulfilmentType: (e.target.value || null) as FulfilmentType | null })}>
                    <option value="" disabled>{autoFulfilment.reason ? 'Multiple methods -- choose' : 'Choose…'}</option>
                    <option value="COLLECTION_ONLY">Collection Only</option>
                    <option value="COLLECTION_AND_DELIVERY">Collection + Delivery</option>
                  </select>
                  {fulfilmentIsOverridden ? (
                    <button onClick={() => onChange({ fulfilmentType: null })} title="Reset to suggested value"
                      className="text-amber-500 hover:text-amber-600">✏️</button>
                  ) : autoFulfilment.type ? (
                    <span title={autoFulfilment.reason || 'Auto-suggested'} className="text-gray-300">🔄</span>
                  ) : null}
                </div>
                {autoFulfilment.reason && !fulfilmentIsOverridden && (
                  <div className="mt-0.5 text-[8px] text-amber-600 dark:text-amber-400 max-w-[200px]">{autoFulfilment.reason}</div>
                )}
              </div>
              <div className="flex gap-6 text-[10px]">
                <div><div className="text-gray-500">Customer Ref</div>
                  <EditableField value={draft.customerRef} onCommit={v => onChange({ customerRef: v })} editor={p => <InlineText {...p} />} /></div>
                <div><div className="text-gray-500">Job Reference</div>
                  <div className="font-bold" title={plain ? 'The job this note belongs to -- not editable here' : 'From the devanning -- not editable here'}>{jobRef}</div></div>
              </div>
            </div>

            {/* Address block -- Collect From always auto-fills from the devanning's warehouse
                (overridable/resettable); Release To is who takes it from us (courier/self/
                supplier/own driver), always manual; Deliver To only appears for a
                Collection+Delivery job (final consignee, no auto-default). */}
            <div className="flex border border-black mb-2 text-[10px]">
              <div className="flex-1 border-r border-black p-1.5">
                <div className="flex justify-between">
                  <span className="font-bold">Collect From:</span>
                  <span className="text-right">
                    <div className="font-bold">Collection Details</div>
                    <EditableField value={draft.scheduledDate} display={fmtDate(draft.scheduledDate)} onCommit={v => onChange({ scheduledDate: v })} editor={p => <InlineDate {...p} />} />
                  </span>
                </div>
                <AddressField value={draft.collectFrom} autoValue={autoWarehouseAddress}
                  onCommit={v => onChange({ collectFrom: v })} />
              </div>
              <div className={`flex-1 p-1.5 ${showDeliverTo ? 'border-r border-black' : ''}`}>
                <div className="font-bold">Release To:</div>
                <AddressField value={draft.releaseTo} onCommit={v => onChange({ releaseTo: v })} />
                {draft.releaseTo.name && (
                  <div className="mt-1">Telephone: <EditableField value={draft.releaseTo.phone || ''} onCommit={v => onChange({ releaseTo: { ...draft.releaseTo, phone: v } })} editor={p => <InlineText {...p} />} /></div>
                )}
                {isSelfOrSupplier && (
                  <>
                    <div className="mt-1">Vehicle: <EditableField value={draft.vehicleReg} onCommit={v => onChange({ vehicleReg: v })} editor={p => <InlineText {...p} />} /></div>
                    <div>Driver: <EditableField value={draft.driverName} onCommit={v => onChange({ driverName: v })} editor={p => <InlineText {...p} />} /></div>
                  </>
                )}
              </div>
              {showDeliverTo && (
                <div className="flex-1 p-1.5">
                  <div className="font-bold">Deliver To:</div>
                  <AddressField value={draft.deliverTo} onCommit={v => onChange({ deliverTo: v })} />
                  {draft.deliverTo.name && (
                    <div className="mt-1">Telephone: <EditableField value={draft.deliverTo.phone || ''} onCommit={v => onChange({ deliverTo: { ...draft.deliverTo, phone: v } })} editor={p => <InlineText {...p} />} /></div>
                  )}
                </div>
              )}
            </div>


            {/* Goods table */}
            <table className="w-full border border-black text-[9px] mb-2">
              <thead>
                <tr className="font-bold">
                  <td className="border-r border-black p-1 w-1/4">Marks &amp; Numbers</td>
                  <td className="border-r border-black p-1">Description</td>
                  <td className="border-r border-black p-1 text-right w-12">NOP</td>
                  <td className="border-r border-black p-1 text-right w-14">Weight</td>
                  <td className="border-r border-black p-1 text-right w-12">Cube</td>
                  <td className="p-1 w-16">Summary</td>
                </tr>
              </thead>
              <tbody>
                <tr className="align-top" style={{ height: 90 }}>
                  <td className="border-r border-t border-black p-1">
                    <EditableField value={draft.marks} display={marksDisplay} onCommit={updateMarksLine} editor={p => <InlineTextArea {...p} rows={3} />} />
                  </td>
                  <td className="border-r border-t border-black p-1">
                    <AutoField plain={plain} auto={autoDescription} override={draft.descriptionOverride}
                      onOverride={v => onChange({ descriptionOverride: v })}
                      onReset={() => onChange({ descriptionOverride: null })} editor={p => <InlineTextArea {...p} />} />
                  </td>
                  <td className="border-r border-t border-black p-1 text-right font-bold">
                    <AutoField plain={plain} auto={String(autoNop)} override={draft.nopOverride === null ? null : String(draft.nopOverride)}
                      onOverride={v => onChange({ nopOverride: v === '' ? autoNop : Number(v) })}
                      onReset={() => onChange({ nopOverride: null })} editor={p => <InlineNumber {...p} />} />
                  </td>
                  <td className="border-r border-t border-black p-1 text-right">
                    <AutoField plain={plain} auto={autoWeightKg != null ? autoWeightKg.toFixed(1) : ''} override={draft.weightKgOverride === null ? null : String(draft.weightKgOverride)}
                      onOverride={v => onChange({ weightKgOverride: v === '' ? null : Number(v) })}
                      onReset={() => onChange({ weightKgOverride: null })} editor={p => <InlineNumber {...p} />} />
                  </td>
                  <td className="border-r border-t border-black p-1 text-right">
                    <AutoField plain={plain} auto={autoCbm != null ? autoCbm.toFixed(2) : ''} override={draft.cbmOverride === null ? null : String(draft.cbmOverride)}
                      onOverride={v => onChange({ cbmOverride: v === '' ? null : Number(v) })}
                      onReset={() => onChange({ cbmOverride: null })} editor={p => <InlineNumber {...p} />} />
                  </td>
                  <td className="border-t border-black p-1">
                    <AutoField plain={plain} auto={autoSummary} override={draft.summaryOverride}
                      onOverride={v => onChange({ summaryOverride: v })}
                      onReset={() => onChange({ summaryOverride: null })} editor={p => <InlineText {...p} />} />
                  </td>
                </tr>
              </tbody>
            </table>

            {/* Remarks */}
            <div className="font-bold underline text-[10px]">Remarks:</div>
            <div className="border border-black p-1.5 mb-2 min-h-[32px] text-[10px]">
              <AutoField plain={plain} auto={autoRemarks} override={draft.remarksOverride} onOverride={v => onChange({ remarksOverride: v })}
                onReset={() => onChange({ remarksOverride: null })} editor={p => <InlineTextArea {...p} />} />
            </div>

            <div className="font-bold underline text-[10px]">Notes:</div>
            <div className="border border-black p-1.5 mb-2 min-h-[32px] text-[10px]">
              <EditableField value={draft.notes} onCommit={v => onChange({ notes: v })} editor={p => <InlineTextArea {...p} />} />
            </div>

            {/* Authorisation -- read-only Serial No, editable Authorise To line */}
            <div className="border border-black mb-2 text-[10px]">
              <div className="flex justify-between items-center border-b border-black p-1.5">
                <span className="font-bold">
                  I AUTHORISE YOU TO RELEASE THESE GOODS TO: {' '}
                  <AutoField plain={plain} auto={autoAuthoriseTo.toUpperCase()} override={draft.authoriseToOverride ? draft.authoriseToOverride.toUpperCase() : null}
                    onOverride={v => onChange({ authoriseToOverride: v })} onReset={() => onChange({ authoriseToOverride: null })}
                    editor={p => <InlineText {...p} />} />
                </span>
                <span className="font-bold">Serial No: {serialNo ?? '(assigned on generate)'}</span>
              </div>
              <div className="grid grid-cols-3 border-b border-black text-[8px] text-gray-400 p-1.5">
                <span>SIGNATURE</span><span>PRINT NAME</span><span>TIME/DATE</span>
              </div>
              <div className="grid grid-cols-3 border-b border-black p-1.5 text-gray-400" title="Auto-filled with the issuing staff member and the exact time this note is generated -- not editable (audit trail)">
                <span></span>
                <span className="italic">{issuerName || '—'}</span>
                <span className="italic">{fmtIssuedAt(new Date().toISOString())}</span>
              </div>
              <div className="font-bold p-1.5 border-b border-black">RECEIVED IN GOOD CONDITION</div>
              <div className="grid grid-cols-3 text-[8px] text-gray-400 p-1.5">
                <span>SIGNATURE</span><span>PRINT NAME</span><span>TIME/DATE</span>
              </div>
            </div>

            <div className="text-[7px] text-gray-500 text-center border-t pt-1">
              {companyRegistrationLine(company)} {company.bifa_line}
            </div>
          </div>
        )}
        <p className="text-xs text-gray-400 text-center mt-2">Preview matches this exact PDF -- click any field above to edit{plain ? '.' : ', changes autosave.'}</p>
      </div>

      {!plain && (<>
      {/* ── Data Sources sidebar ── */}
      <div className="w-full md:w-56 flex-shrink-0">
        <div className="bg-gray-50 dark:bg-gray-900/40 rounded-xl border border-gray-200 dark:border-gray-700 p-3 sticky top-4">
          <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">Data Sources</h4>
          {(() => {
            const overridden = AUTO_FIELD_KEYS.filter(k => {
              if (k === 'nop') return draft.nopOverride !== null
              if (k === 'weightKg') return draft.weightKgOverride !== null
              if (k === 'cbm') return draft.cbmOverride !== null
              if (k === 'remarks') return draft.remarksOverride !== null
              if (k === 'description') return draft.descriptionOverride !== null
              if (k === 'summary') return draft.summaryOverride !== null
              return draft.authoriseToOverride !== null
            })
            const values: Record<AutoFieldKey, string | number | null> = {
              nop, weightKg, cbm, remarks, authoriseTo, description, summary,
            }
            const empty = AUTO_FIELD_KEYS.filter(k => !overridden.includes(k) && !values[k])
            const auto = AUTO_FIELD_KEYS.filter(k => !overridden.includes(k) && !empty.includes(k))

            // The one address field with a real "smart default" -- Collect From always starts
            // pre-filled with the devanning's own warehouse (Release To / Deliver To always
            // start empty, no sensible auto-default for either).
            const addressLabel = 'Collect From'
            const addressVal = draft.collectFrom
            const addressIsEmpty = !addressVal.name
            const addressIsAuto = !addressIsEmpty && !!autoWarehouseAddress && addressVal.warehouseId === autoWarehouseAddress.warehouseId
            const addressIsOverridden = !addressIsEmpty && !addressIsAuto
            const resetAddress = () => onChange({ collectFrom: autoWarehouseAddress || blankAddress() })

            const autoCount = auto.length + (addressIsAuto ? 1 : 0)
            const overriddenCount = overridden.length + (addressIsOverridden ? 1 : 0)
            const emptyCount = empty.length + (addressIsEmpty ? 1 : 0)

            return (
              <>
                <div className="grid grid-cols-3 gap-1 text-center mb-3">
                  <div className="bg-white dark:bg-gray-800 rounded-lg py-1.5"><div className="text-sm font-bold text-blue-600">{autoCount}</div><div className="text-[9px] text-gray-400">Auto</div></div>
                  <div className="bg-white dark:bg-gray-800 rounded-lg py-1.5"><div className="text-sm font-bold text-amber-500">{overriddenCount}</div><div className="text-[9px] text-gray-400">Overridden</div></div>
                  <div className="bg-white dark:bg-gray-800 rounded-lg py-1.5"><div className="text-sm font-bold text-gray-400">{emptyCount}</div><div className="text-[9px] text-gray-400">Empty</div></div>
                </div>
                <div className="space-y-1.5">
                  {AUTO_FIELD_KEYS.map(k => {
                    const isOverridden = overridden.includes(k)
                    const isEmpty = empty.includes(k)
                    const reset = () => {
                      if (k === 'nop') onChange({ nopOverride: null })
                      else if (k === 'weightKg') onChange({ weightKgOverride: null })
                      else if (k === 'cbm') onChange({ cbmOverride: null })
                      else if (k === 'remarks') onChange({ remarksOverride: null })
                      else if (k === 'description') onChange({ descriptionOverride: null })
                      else if (k === 'summary') onChange({ summaryOverride: null })
                      else onChange({ authoriseToOverride: null })
                    }
                    return (
                      <div key={k} className="flex items-center justify-between text-xs">
                        <span className={isEmpty ? 'text-gray-400' : 'text-gray-700 dark:text-gray-300'}>{AUTO_FIELD_LABELS[k]}</span>
                        {isOverridden ? (
                          <button onClick={reset} title="Manually overridden -- click to restore auto value" className="text-amber-500 hover:text-amber-600">✏️</button>
                        ) : (
                          <span title={isEmpty ? 'No auto value available yet' : 'Auto-computed'} className="text-gray-300">🔄</span>
                        )}
                      </div>
                    )
                  })}
                  {autoWarehouseAddress && (
                    <div className="flex items-center justify-between text-xs">
                      <span className={addressIsEmpty ? 'text-gray-400' : 'text-gray-700 dark:text-gray-300'}>{addressLabel}</span>
                      {addressIsOverridden ? (
                        <button onClick={resetAddress} title="Manually overridden -- click to restore warehouse default" className="text-amber-500 hover:text-amber-600">✏️</button>
                      ) : (
                        <span title={addressIsEmpty ? 'No auto value available yet' : 'Auto-filled from warehouse'} className="text-gray-300">🔄</span>
                      )}
                    </div>
                  )}
                </div>
              </>
            )
          })()}
          {containerNo && <p className="text-[10px] text-gray-400 mt-3">Container {containerNo}</p>}
        </div>
      </div>
      </>)}
    </div>
  )
}
